import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { products } from '../mock/mockData';
import { useApplicationModal } from '../context/ApplicationModalContext';
import { SEO, buildBreadcrumbLd } from '../components/SEO';
import {
  Wallet, Car, Gem, ArrowRight, CheckCircle2, FileCheck, Check
} from 'lucide-react';

const iconMap = { Wallet, Car, Gem };

// Per-product SEO metadata (single source of truth for titles + descriptions)
const PRODUCT_SEO = {
  'istehlak-kreditleri': {
    title: 'İstehlak krediti | Şərtlər və müraciət | İDK BOKT',
    description: 'İDK BOKT-nin istehlak krediti — şəxsi ehtiyaclarınız üçün onlayn müraciət, şəffaf şərtlər və rahat ödəniş qrafiki.',
  },
  'qizil-kreditleri': {
    title: 'Qızıl krediti | Şərtlər və müraciət | İDK BOKT',
    description: 'Qızıl girovu qarşılığında sürətli kredit həlli. Qiymətləndirmə, təhlükəsiz saxlanma və şəffaf şərtlər.',
  },
  'avto-lizinq': {
    title: 'Avto lizinq | Şərtlər və müraciət | İDK BOKT',
    description: 'Avtomobil lizinqi ilə xəyalınızdakı avtomobili əldə edin. Uzun müddət, rahat ödəniş qrafiki və şəffaf şərtlər.',
  },
};

export const ProductDetailPage = () => {
  const { slug } = useParams();
  const product = products.find(p => p.slug === slug);
  const { openModal } = useApplicationModal();

  if (!product) {
    return <Navigate to="/" replace />;
  }

  const Icon = iconMap[product.icon];
  const hasDetails = !!product.details;
  const otherProducts = products.filter(p => p.slug !== slug);
  // Only İstehlak Krediti supports online application. For other products
  // customers are advised to call — the phone number is in the top bar.
  const isApplyEnabled = slug === 'istehlak-kreditleri';
  const seoMeta = PRODUCT_SEO[slug] || {
    title: `${product.title} | İDK BOKT`,
    description: product.description,
  };
  const breadcrumbLd = buildBreadcrumbLd([
    { name: 'Əsas səhifə', path: '/' },
    { name: 'Məhsullar', path: `/mehsullar/${slug}` },
    { name: product.title, path: `/mehsullar/${slug}` },
  ]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/50 via-white to-white">
      <SEO
        title={seoMeta.title}
        description={seoMeta.description}
        path={`/mehsullar/${slug}`}
        jsonLd={breadcrumbLd}
      />
      {/* Hero — compact editorial header */}
      <section className="pt-10 pb-6 lg:pt-12 lg:pb-8">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 leading-tight font-serif-display">
            {product.title}
          </h1>
          {hasDetails && (
            <p className="text-base sm:text-lg text-gray-700 leading-snug mb-2 max-w-2xl mx-auto">
              {product.details.heroTitle}
            </p>
          )}
          <p className="text-sm text-gray-500 leading-relaxed max-w-2xl mx-auto">
            {hasDetails ? product.details.heroDescription : product.description}
          </p>
        </div>
      </section>

      {/* Unified premium detail panel — terms + requirements in one card */}
      {hasDetails && (
        <section className="pb-14 lg:pb-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <div className="relative rounded-3xl bg-white ring-1 ring-gray-200/70 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_24px_60px_-24px_rgba(15,23,42,0.18)] overflow-hidden">
              {/* Terms strip */}
              {product.details.terms && (
                <div className="grid grid-cols-1 sm:grid-cols-3 sm:divide-x sm:divide-gray-200 border-b border-gray-200">
                  {product.details.terms.map((term) => {
                    const m = term.value.match(/^([\d\s.,%]+)(.*)$/);
                    const headline = m ? m[1].trim() : term.value;
                    const unit = m ? m[2].trim() : '';
                    return (
                      <div
                        key={term.label}
                        className="px-6 py-7 sm:px-8 sm:py-8 text-center"
                      >
                        <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-[0.18em] mb-2.5">
                          {term.label}
                        </p>
                        <p className="text-2xl sm:text-[28px] font-bold text-gray-900 tabular-nums leading-none font-serif-display">
                          {headline}
                        </p>
                        {unit && (
                          <p className="text-xs text-gray-500 mt-2 leading-snug">
                            {unit}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Requirements body */}
              {product.details.sections && (
                <div className="px-6 sm:px-10 py-8 sm:py-10">
                  <div
                    className={`grid grid-cols-1 gap-y-8 items-start ${
                      product.details.sections.length === 1
                        ? 'md:max-w-md md:mx-auto'
                        : 'md:grid-cols-2 md:gap-x-10 lg:gap-x-14'
                    }`}
                  >
                    {product.details.sections.map((section) => (
                      <div key={section.title}>
                        <h2 className="text-[12px] font-semibold text-gray-900 uppercase tracking-[0.14em] mb-4 pb-3 border-b border-gray-100">
                          {section.title}
                        </h2>
                        <ul className="space-y-2.5 text-[14.5px] text-gray-700 leading-relaxed">
                          {section.items.map((item) => (
                            <li key={item} className="flex gap-2.5">
                              <Check
                                aria-hidden="true"
                                className="h-4 w-4 text-blue-600 flex-shrink-0 mt-1 stroke-[2.5]"
                              />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer CTA (only İstehlak) */}
              {isApplyEnabled && (
                <div className="border-t border-gray-200 bg-gray-50/60 px-6 sm:px-10 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <p className="text-sm text-gray-600">
                    Kreditə ilkin müraciət 2 dəqiqədə — online formu doldurun.
                  </p>
                  <Button
                    size="lg"
                    className="bg-blue-700 hover:bg-blue-800 text-white px-7 py-5 text-sm font-semibold w-full sm:w-auto"
                    data-testid="detail-apply-btn"
                    onClick={() => openModal()}
                  >
                    Müraciət et
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Features (only if no rich details) */}
      {!hasDetails && (
        <section className="py-14 lg:py-16">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <span className="text-xs font-semibold text-blue-700 uppercase tracking-[0.2em]">Üstünlüklər</span>
              <h2 className="text-3xl font-bold text-gray-900 mt-3">Əsas üstünlüklər</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {product.features.map((feature, idx) => (
                <div key={feature} className="flex items-start gap-4 p-6 bg-gray-50 rounded-xl border border-gray-100">
                  <CheckCircle2 className="h-6 w-6 text-blue-700 flex-shrink-0 mt-0.5" />
                  <p className="text-base text-gray-900 font-medium">{feature}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Placeholder for products without details */}
      {!hasDetails && (
        <section className="py-12 bg-gray-50 border-t border-gray-200">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <Card className="border border-dashed border-gray-300 bg-white">
              <CardContent className="p-7 sm:p-8 text-center">
                <FileCheck className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Ətraflı məlumat tezliklə</h3>
                <p className="text-sm text-gray-600 max-w-md mx-auto">
                  Bu məhsul haqqında ətraflı şərtlər, sənəd tələbləri və faiz dərəcələri tezliklə əlavə olunacaq.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Other Products — compact pills */}
      {otherProducts.length > 0 && (
        <section className="border-t border-gray-100 py-8">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
              <span className="text-sm text-gray-500 flex-shrink-0">Digər məhsullar:</span>
              <div className="flex flex-wrap gap-2">
                {otherProducts.map((p) => {
                  const OtherIcon = iconMap[p.icon];
                  return (
                    <Link
                      key={p.id}
                      to={`/mehsullar/${p.slug}`}
                      data-testid={`other-product-${p.slug}`}
                      className="group inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-blue-100 text-sm font-medium text-gray-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                    >
                      <OtherIcon className="h-4 w-4 text-blue-700" />
                      <span>{p.title}</span>
                      <ArrowRight className="h-3.5 w-3.5 text-gray-400 group-hover:text-blue-700 transition-colors" />
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
